/**
 * SA Expired Sale Popup — date-gated, non-dismissable "sale has ended" interstitial with
 * Klaviyo email capture and an auto-redirect countdown (DTC-1349).
 * Author: Plaud Sharon
 * Version: 1.0.1 - 2026-09-20 - Plaud Sharon — focus only cancels the countdown; input clears the error (fix: error vanished on refocus)
 * Version-note: 1.0.0 - 2026-09-20 - Plaud Sharon — initial
 *
 * Flow: [connected] → resolve trigger (always | end_time boundary timer + visibilitychange
 * re-check) → show (scroll lock, inert the other sections, focus dialog, GA4 view) → countdown
 * → location.replace(redirect). Email focus/input cancels the countdown; Notify me validates,
 * posts to the Klaviyo client subscriptions API, flips to the success state and starts the
 * shorter success countdown. Nothing here ever closes the popup — by design.
 *
 * GA4: reuses the ga4-push contract ({event:'ga4Event', event_name, event_parameters}) through
 * window.pushGa4Event when the store snippet defines it, else pushes the same shape to dataLayer.
 */
(function () {
  'use strict';
  if (customElements.get('sa-expired-sale-popup')) return;

  var MAX_TIMEOUT = 2147483647; // setTimeout ceiling (~24.8 days) — longer waits are chained
  var REDIRECT_GRACE_MS = 300;  // let the auto_redirect dataLayer push leave before navigating
  var KLAVIYO_TIMEOUT_MS = 8000;
  var KLAVIYO_REVISION = '2025-10-15'; // same as theme.js klaviyo-newsletter
  var MODULE_NAME = 'expired_sale_popup';
  // strict: date, 'T', time (seconds optional), mandatory zone (Z or ±hh:mm). No spaces, no 1-digit parts.
  var ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:Z|[+-]\d{2}:\d{2})$/;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function parseEndTime(str) {
    if (!str || !ISO_RE.test(str)) return NaN;
    var t = new Date(str).getTime();
    return isNaN(t) ? NaN : t;
  }

  function ga4(eventName, params) {
    var body = { event: 'ga4Event', event_name: eventName, event_parameters: params };
    try {
      if (typeof window.pushGa4Event === 'function') { window.pushGa4Event(body); return; }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event_parameters: null });
      window.dataLayer.push(body);
    } catch (e) { /* analytics must never break the popup */ }
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SaExpiredSalePopup extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.dialog = this.querySelector('[data-esp-dialog]');
      this.formState = this.querySelector('[data-esp-state="form"]');
      this.successState = this.querySelector('[data-esp-state="success"]');
      this.form = this.querySelector('[data-esp-form]');
      this.emailInput = this.querySelector('[data-esp-email]');
      this.errorEl = this.querySelector('[data-esp-error]');
      this.submitBtn = this.querySelector('[data-esp-submit]');
      this.countdownEl = this.querySelector('[data-esp-countdown]');
      this.successCountdownEl = this.querySelector('[data-esp-countdown-success]');
      this.successTitle = this.querySelector('[data-esp-success-title]');

      this.redirectUrl = this.dataset.redirectUrl || '/';
      this.redirectSeconds = Math.max(1, parseInt(this.dataset.redirectSeconds, 10) || 10);
      this.successSeconds = Math.max(1, parseInt(this.dataset.successSeconds, 10) || 3);
      this.designMode = this.dataset.designMode === 'true';

      this._shown = false;
      this._cancelled = false;
      this._countdownTimer = null;
      this._boundaryTimer = null;
      this._inerted = [];

      this._onEmailFocus = this._onEmailFocus.bind(this);
      this._onEmailInput = this._onEmailInput.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
      this._onPageShow = this._onPageShow.bind(this);

      if (this.emailInput) {
        this.emailInput.addEventListener('focus', this._onEmailFocus);
        this.emailInput.addEventListener('input', this._onEmailInput);
      }
      if (this.form) this.form.addEventListener('submit', this._onSubmit);
      this.addEventListener('keydown', this._onKeydown);
      window.addEventListener('pageshow', this._onPageShow);

      this._arm();
    }

    disconnectedCallback() {
      clearTimeout(this._boundaryTimer);
      this._stopCountdown();
      document.removeEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('pageshow', this._onPageShow);
      this._releasePage();
      this._bound = false;
    }

    /* ---------- trigger ---------- */

    _arm() {
      var mode = this.dataset.mode;
      if (mode === 'always') { this._show(); return; }
      if (mode !== 'end_time') return;

      this._target = parseEndTime(this.dataset.endTime);
      if (isNaN(this._target)) {
        console.warn('[sa-expired-sale-popup] End time missing or not in yyyy-mm-ddThh:mm:ss+08:00 format — popup disabled:', JSON.stringify(this.dataset.endTime || ''));
        return;
      }
      document.addEventListener('visibilitychange', this._onVisibility);
      this._scheduleBoundary();
    }

    _scheduleBoundary() {
      var delay = this._target - Date.now();
      if (delay <= 0) { this._show(); return; }
      clearTimeout(this._boundaryTimer);
      this._boundaryTimer = setTimeout(this._scheduleBoundary.bind(this), Math.min(delay, MAX_TIMEOUT));
    }

    _onVisibility() {
      if (!document.hidden && !this._shown && Date.now() >= this._target) this._show();
    }

    _onPageShow(e) {
      // bfcache restore after a redirect + Back: timers were frozen; restart the countdown
      // unless the visitor had already cancelled it by touching the email field.
      if (e && e.persisted && this._shown && !this._cancelled && !this._countdownTimer) {
        this._startCountdown(this.redirectSeconds, this.countdownEl, 'timeout');
      }
    }

    /* ---------- show ---------- */

    _show() {
      if (this._shown) return;
      this._shown = true;
      clearTimeout(this._boundaryTimer);
      document.removeEventListener('visibilitychange', this._onVisibility);

      this._lockPage();
      this.hidden = false;
      if (this.dialog) {
        try { this.dialog.focus({ preventScroll: true }); } catch (e) { this.dialog.focus(); }
      }
      ga4('view_banner', { module_name: MODULE_NAME, title: this.dataset.title || '' });
      this._startCountdown(this.redirectSeconds, this.countdownEl, 'timeout');
    }

    _lockPage() {
      var root = document.documentElement;
      var scrollbar = window.innerWidth - root.clientWidth;
      if (scrollbar > 0) root.style.setProperty('--sa-esp-scrollbar', scrollbar + 'px');
      root.classList.add('sa-esp-open');

      // Everything else on the page becomes inert (mouse + keyboard + AT), so the expired
      // promo CTAs underneath cannot be used. Third-party widgets outside .shopify-section
      // (chat etc.) are intentionally left alone.
      var own = this.closest('.shopify-section');
      var sections = document.querySelectorAll('.shopify-section');
      for (var i = 0; i < sections.length; i++) {
        var s = sections[i];
        if (s === own || s.contains(this)) continue;
        if (s.hasAttribute('inert')) continue;
        s.setAttribute('inert', '');
        this._inerted.push(s);
      }
    }

    _releasePage() {
      var root = document.documentElement;
      root.classList.remove('sa-esp-open');
      root.style.removeProperty('--sa-esp-scrollbar');
      for (var i = 0; i < this._inerted.length; i++) this._inerted[i].removeAttribute('inert');
      this._inerted = [];
    }

    _onKeydown(e) {
      // no Escape close (by design); keep Tab cycling inside the dialog
      if (e.key !== 'Tab' || !this.dialog) return;
      var focusable = Array.prototype.filter.call(this.dialog.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null; // visible only (hidden state is display:none)
      });
      if (!focusable.length) { e.preventDefault(); return; }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === this.dialog || !this.dialog.contains(active)) { e.preventDefault(); last.focus(); }
      } else if (active === last || !this.dialog.contains(active)) {
        e.preventDefault(); first.focus();
      }
    }

    /* ---------- countdown / redirect ---------- */

    _startCountdown(seconds, el, redirectType) {
      this._stopCountdown();
      this._remaining = seconds;
      this._renderTime(el);
      if (el) el.hidden = false;
      var self = this;
      this._countdownTimer = setInterval(function () {
        self._remaining -= 1;
        self._renderTime(el);
        if (self._remaining <= 0) {
          self._stopCountdown();
          self._redirect(redirectType);
        }
      }, 1000);
    }

    _renderTime(el) {
      if (!el) return;
      var span = el.querySelector('[data-esp-time]');
      if (span) span.textContent = String(Math.max(0, this._remaining));
    }

    _stopCountdown() {
      if (this._countdownTimer) { clearInterval(this._countdownTimer); this._countdownTimer = null; }
    }

    _cancelCountdown() {
      if (this._cancelled) return;
      this._cancelled = true;
      this._stopCountdown();
      if (this.countdownEl) this.countdownEl.hidden = true;
    }

    _redirect(redirectType) {
      ga4('auto_redirect', { module_name: MODULE_NAME, redirect_type: redirectType, url: this.redirectUrl });
      if (this.designMode) {
        console.info('[sa-expired-sale-popup] Theme editor: redirect skipped →', this.redirectUrl);
        return;
      }
      var url = this.redirectUrl;
      // replace(): no history entry, so Back does not land on the expired page and re-redirect
      setTimeout(function () { window.location.replace(url); }, REDIRECT_GRACE_MS);
    }

    /* ---------- email ---------- */

    // focus (click / Tab into the field) only cancels the auto redirect — it must NOT clear an
    // error, because we move focus back into the field right after showing one
    _onEmailFocus() {
      this._cancelCountdown();
    }

    _onEmailInput() {
      this._cancelCountdown();
      if (this.emailInput && this.emailInput.classList.contains('is-invalid')) this._clearError();
    }

    _showError(kind) {
      if (!this.errorEl) return;
      this.errorEl.textContent = kind === 'invalid' ? (this.errorEl.dataset.msgInvalid || '') : (this.errorEl.dataset.msgFailed || '');
      this.errorEl.hidden = false;
      if (this.emailInput) {
        this.emailInput.classList.add('is-invalid');
        this.emailInput.setAttribute('aria-invalid', 'true');
      }
    }

    _clearError() {
      if (this.errorEl) { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
      if (this.emailInput) {
        this.emailInput.classList.remove('is-invalid');
        this.emailInput.removeAttribute('aria-invalid');
      }
    }

    _setLoading(on) {
      if (!this.submitBtn) return;
      this.submitBtn.classList.toggle('is-loading', on);
      this.submitBtn.disabled = on;
      this.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    _onSubmit(e) {
      e.preventDefault();
      this._cancelCountdown();
      if (this.submitBtn && this.submitBtn.classList.contains('is-loading')) return;

      var email = this.emailInput ? this.emailInput.value.trim() : '';
      if (!EMAIL_RE.test(email)) {
        this._showError('invalid');
        if (this.emailInput) this.emailInput.focus();
        return;
      }
      this._clearError();
      this._setLoading(true);

      var self = this;
      this._subscribe(email).then(function () {
        ga4('subscribe', { button_name: 'notify me', status: 'success', module_name: MODULE_NAME });
        self._showSuccess();
      }).catch(function (err) {
        console.error('[sa-expired-sale-popup] Klaviyo subscribe failed:', err);
        ga4('subscribe', { button_name: 'notify me', status: 'fail', module_name: MODULE_NAME });
        self._showError('failed');
        if (self.emailInput) self.emailInput.focus();
      }).then(function () {
        self._setLoading(false);
      });
    }

    _subscribe(email) {
      var key = this.dataset.klaviyoKey;
      var list = this.dataset.klaviyoList;
      if (!key || !list) return Promise.reject(new Error('Klaviyo key/list not configured'));
      var source = this.dataset.klaviyoSource || MODULE_NAME;

      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: source,
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email, properties: { $source: source } }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: list } } }
        }
      };

      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, KLAVIYO_TIMEOUT_MS) : null;

      return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          'accept': 'application/vnd.api+json',
          'revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      }).then(function (res) {
        if (res.ok) return res; // 202 Accepted (double opt-in lists still send a confirmation mail)
        return res.text().then(function (t) { throw new Error('HTTP ' + res.status + ' ' + t.slice(0, 300)); });
      }).finally(function () {
        if (timer) clearTimeout(timer);
      });
    }

    _showSuccess() {
      if (this.formState) this.formState.hidden = true;
      if (this.successState) this.successState.hidden = false;
      if (this.successTitle) {
        try { this.successTitle.focus({ preventScroll: true }); } catch (e) { this.successTitle.focus(); }
      }
      // success state gets its own (short) countdown regardless of the earlier cancellation
      this._cancelled = false;
      this._startCountdown(this.successSeconds, this.successCountdownEl, 'post_signup');
    }
  }

  customElements.define('sa-expired-sale-popup', SaExpiredSalePopup);
})();
